import React, { useRef, useState, useContext } from 'react';
import Table from 'react-bootstrap/Table';
import QuoteModal from './QuoteModal';
import DeleteButton from '../Generic/DeleteButton';
import { deleteQuote, getQuoteDetailsLazy } from '../../clients/quote_client';
import { Link } from 'react-bootstrap-icons';
import S3MediaLink from '../Generic/S3MediaLink';
import './QuoteComponentStyle.css';
import QuoteDetailModal from './QuoteDetailModal';
import OrderDetailModal from '../Order/OrderDetailModal';
import SupplierDetailModal from '../Supplier/SupplierDetailModal';
import SupplierCreditDetailModal from '../SupplierCredit/SupplierCreditDetailModal';
import ProductDetailModal from '../Product/ProductDetailModal';
import { Accordion } from 'react-bootstrap';
import {
  calculatePriceAfterDiscount,
  formatDateStr,
  formatDecimalNumber,
  getCurrencySymbol,
  resolveEntityId,
} from '../../config_and_helpers/helpers';
import { CURRENCY_SYMBOLS } from '../../config_and_helpers/config';
import { AppContext } from '../../App';
import { useIsLabViewer } from '../../hooks/useIsLabViewer';
import SortableHeaderCell from '../Generic/SortableHeaderCell';
import { logError } from '../../config_and_helpers/logger';
import { formatQuoteBudgetDisplay } from '../../utils/quoteFundingDisplay';

/**
 * Renders a table of quotes with their details and associated actions.
 *
 * @param {Object} props - The props passed to the QuoteTable component.
 * @param {Array} props.quoteList - The list of quotes to be displayed.
 * @param {Function} props.handleDelete - The function to handle edit actions for a quote.
 * @param {Function} props.clearSearchValue - The function to clear the search value.
 *
 * @returns {JSX.Element} The JSX markup for the QuoteTable component.
 */
const QuoteTable = ({
  quoteList,
  handleDelete,
  clearSearchValue,
  supplierSelectList,
  onProjectUpdate, // Optional callback to refresh project data after quote operations
  sortBy = '',
  sortDirection = 'asc',
  onSort = null,
}) => {
  const { token } = useContext(AppContext);
  const isViewer = useIsLabViewer();

  // State to store quotes with full data for editing
  const [quotesWithFullData, setQuotesWithFullData] = useState({});
  const [loadingQuotes, setLoadingQuotes] = useState({});

  // Keep the fetched orders state for caching fetched data
  const [fetchedOrders, setFetchedOrders] = useState([]);
  const fetchedOrdersRef = useRef(new Set()); // Ref storing the order Ids to prevent duplicate requests

  // Keep the fetched suppliers state for caching fetched data
  const [fetchedSuppliers, setFetchedSuppliers] = useState([]);
  const fetchedSuppliersRef = useRef(new Set()); // Ref storing the supplier Ids to prevent duplicate requests

  // Function to fetch full quote data for editing
  const fetchFullQuoteData = async quoteId => {
    // Don't fetch if already loading or already fetched
    if (loadingQuotes[quoteId] || quotesWithFullData[quoteId]) {
      return;
    }

    setLoadingQuotes(prev => ({ ...prev, [quoteId]: true }));

    try {
      const fullQuoteData = await getQuoteDetailsLazy(token, quoteId);

      setQuotesWithFullData(prev => ({
        ...prev,
        [quoteId]: fullQuoteData,
      }));
    } catch (error) {
      logError('QuoteTable', 'fetchFullQuoteData failed', error);
    } finally {
      setLoadingQuotes(prev => ({ ...prev, [quoteId]: false }));
    }
  };

  // Function to get the best available quote data (full data if available, otherwise list data)
  const getQuoteDataForEditing = quote => {
    return quotesWithFullData[quote.id] || quote;
  };

  return (
    <Table striped bordered hover>
      {/* Table header defining the columns */}
      <thead>
        <tr className="text-center">
          {/* Column headers like ID, Dates, URL, etc. */}
          <th className="text-center">#</th>
          <SortableHeaderCell
            label="ID"
            sortKey="quote_num"
            sortBy={sortBy}
            sortDirection={sortDirection}
            onSort={onSort}
            className="text-center"
          />
          <th className="text-center">Request Date</th>
          <SortableHeaderCell
            label="Creation/Reception Date"
            sortKey="created_at"
            sortBy={sortBy}
            sortDirection={sortDirection}
            onSort={onSort}
            className="text-center"
          />
          <th className="text-center">Last Updated</th>
          <th className="text-center">URL</th>
          <th className="text-center">Order</th>
          <SortableHeaderCell
            label="Supplier"
            sortKey="supplier_name"
            sortBy={sortBy}
            sortDirection={sortDirection}
            onSort={onSort}
            className="text-center"
          />
          <th className="text-center">Status</th>
          <th className="text-center">Demand Reference</th>
          <th className="text-center">Funding</th>
          <SortableHeaderCell
            label="User"
            sortKey="user"
            sortBy={sortBy}
            sortDirection={sortDirection}
            onSort={onSort}
            className="text-center"
          />
          {!isViewer ? <th className="text-center">Actions</th> : null}
        </tr>
      </thead>
      <tbody>
        {/* Mapping over quoteList to create rows for each quote */}
        {quoteList.map((quote, index) => {
          const quoteOrderId = resolveEntityId(quote?.order);
          // Get the order object from fetched orders if it exists
          const fetchedOrderObj = fetchedOrders.find(
            order => order?.id === quoteOrderId
          );

          return (
            <React.Fragment key={quote.id}>
              <tr className="text-center align-middle">
                {/* Rendering individual data cells with quote information */}
                <td className="text-center">{index + 1}</td>
                <td className="text-center">
                  <QuoteDetailModal
                    quoteObj={quote}
                    onProjectUpdate={onProjectUpdate}
                  />
                </td>
                <td className="text-center">
                  {formatDateStr(quote.request_date)}
                </td>
                <td className="text-center">
                  {quote.creation_date
                    ? formatDateStr(quote.creation_date)
                    : ''}
                </td>
                <td className="text-center">
                  {quote.last_updated ? formatDateStr(quote.last_updated) : ''}
                </td>
                <td className="text-center">
                  <S3MediaLink
                    url={quote.quote_url}
                    quoteId={quote.id}
                    title="Open quote PDF"
                  >
                    <Link size={'2rem'} />
                  </S3MediaLink>
                </td>
                <td className="text-center">
                  {quoteOrderId ? (
                    <OrderDetailModal
                      fetchedOrdersRef={fetchedOrdersRef}
                      fetchedOrders={fetchedOrders}
                      setFetchedOrders={setFetchedOrders}
                      orderId={quoteOrderId}
                      updateOrders={handleDelete}
                      {...(fetchedOrderObj
                        ? { orderObj: fetchedOrderObj }
                        : {})}
                    />
                  ) : (
                    <span className="text-muted">No order associated</span>
                  )}
                </td>
                <td className="text-center">
                  <SupplierDetailModal
                    fetchedSuppliersRef={fetchedSuppliersRef}
                    fetchedSuppliers={fetchedSuppliers}
                    setFetchedSuppliers={setFetchedSuppliers}
                    supplierObj={quote.supplier}
                    supplierId={quote.supplier.id}
                  />
                </td>
                <td className="text-center">{quote.status}</td>
                <td className="text-center">{quote.corporate_demand_ref}</td>
                <td className="text-center">
                  {(() => {
                    const fundingCredit =
                      quote.funding_credit ??
                      quote.supplier_credit ??
                      null;
                    const fundingCreditId =
                      (typeof fundingCredit === 'object' && fundingCredit?.id) ||
                      (fundingCredit != null && fundingCredit) ||
                      quote.funding_credit_id ||
                      quote.supplier_credit_id ||
                      null;

                    if (fundingCreditId) {
                      return (
                        <span className="small">
                          <SupplierCreditDetailModal
                            creditObj={
                              typeof fundingCredit === 'object'
                                ? fundingCredit
                                : undefined
                            }
                            creditId={fundingCreditId}
                          />
                        </span>
                      );
                    }

                    return formatQuoteBudgetDisplay(quote);
                  })()}
                </td>
                <td className="text-center">
                  {quote.user ? (
                    <span>
                      {quote.user.first_name} {quote.user.last_name}
                    </span>
                  ) : (
                    <span className="text-muted">-</span>
                  )}
                </td>
                {!isViewer ? (
                <td className="text-center">
                  <div className="d-flex flex-row justify-content-center">
                    <div className="me-2">
                      <QuoteModal
                        quoteObj={getQuoteDataForEditing(quote)}
                        clearSearchValue={clearSearchValue}
                        disableEdit={!!quote.order}
                        supplierSelectList={supplierSelectList}
                        onEditButtonHover={() => fetchFullQuoteData(quote.id)}
                        projectId={resolveEntityId(quote.project)}
                        onProjectUpdate={onProjectUpdate} // Pass refresh callback if provided
                        isEditButtonLoading={loadingQuotes[quote.id]}
                      />
                    </div>
                    <DeleteButton
                      // Disable quote deletion if quote is related to an order
                      disableDelete={!!quote.order}
                      objectType="quote"
                      objectName={quote.id}
                      objectId={quote.id}
                      deleteFetchFunc={deleteQuote}
                      onSuccessfulDelete={handleDelete}
                      clearSearchValue={clearSearchValue}
                    />
                  </div>
                </td>
                ) : null}
              </tr>

              {/* Additional row for accordion with quote items - only show if items are available */}
              {quote.items && quote.items.length > 0 && (
                <tr>
                  {/* Accordion for detailed quote item view */}
                  <td></td>
                  <td>
                    <Accordion flush>
                      {/* Accordion item for quote items */}
                      <Accordion.Item eventKey={0}>
                        <Accordion.Header>Quote Items</Accordion.Header>
                        <Accordion.Body>
                          {/* Table for listing quote items */}
                          <Table striped bordered hover>
                            {/* Table header and body for quote items */}
                            <thead>
                              <tr className="text-center bold-italic-text">
                                <td>#</td>
                                <td>Product</td>
                                <td>Quantity</td>
                                <td>Price</td>
                                <td>Discount</td>
                                <th>Post-Discount Price</th>
                              </tr>
                            </thead>
                            <tbody>
                              {quote.items.map((item, index) => {
                                return (
                                  <React.Fragment key={item.id}>
                                    <tr className="text-center italic-text">
                                      <td>{index + 1}</td>
                                      <td>
                                        <ProductDetailModal
                                          productObj={item.product}
                                          productId={item.product.id}
                                          supplierObj={quote.supplier}
                                        />
                                      </td>
                                      <td>{item.quantity}</td>
                                      <td>
                                        {/* Format and display item's price */}
                                        {formatDecimalNumber(item.price)}
                                        {/* If there is a symbol for the item's currency, get and display the currency symbol */}
                                        {CURRENCY_SYMBOLS[item.currency]
                                          ? `${getCurrencySymbol(
                                              item.currency
                                            )}`
                                          : ''}
                                      </td>
                                      <td>
                                        {/* If there is a discount for the item, format and display the discount as a percentage, else display null */}
                                        {item?.discount !== null
                                          ? `${formatDecimalNumber(
                                              item?.discount
                                            )}%`
                                          : null}
                                      </td>
                                      <td>
                                        {/* If there is a discount for the item, calculate and display the price after discount, else display null */}
                                        {item?.discount !== null
                                          ? formatDecimalNumber(
                                              calculatePriceAfterDiscount(
                                                item.price,
                                                item?.discount
                                              )
                                            )
                                          : null}
                                      </td>
                                    </tr>
                                  </React.Fragment>
                                );
                              })}
                            </tbody>
                          </Table>
                        </Accordion.Body>
                      </Accordion.Item>
                    </Accordion>
                  </td>
                </tr>
              )}
            </React.Fragment>
          );
        })}
      </tbody>
    </Table>
  );
};
export default QuoteTable;
